/**
 * Pure helpers behind the attendance history calendar. No React, no native —
 * unit-tested in __tests__/attendanceCalendar.test.js. Months are "YYYY-MM"
 * strings and all "today" maths is IST, like the rest of the attendance feature.
 */
import { todayIST } from "@utils/attendance";

export const HISTORY_STATUS = Object.freeze({
  COMPLETE: "COMPLETE",
  IN_ONLY: "IN_ONLY",
  ABSENT: "ABSENT",
  PENDING: "PENDING",
  FUTURE: "FUTURE",
});

/** The backend rejects months further back than this. */
export const HISTORY_MAX_MONTHS_BACK = 6;

/** Sunday-first, matching the Gigs history calendar. */
export const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_RE = /^(\d{4})-(\d{2})$/;

const parseMonth = (month) => {
  const match = MONTH_RE.exec(String(month ?? ""));
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  return m >= 1 && m <= 12 ? { y, m } : null;
};

const pad2 = (n) => String(n).padStart(2, "0");

/** Current IST month as "YYYY-MM". */
export const currentMonthIST = (now = new Date()) => todayIST(now).slice(0, 7);

/** "2026-09" shifted by `delta` months. Returns the input if unparseable. */
export const shiftMonth = (month, delta) => {
  const p = parseMonth(month);
  if (!p) return month;
  const idx = p.y * 12 + (p.m - 1) + delta;
  return `${String(Math.floor(idx / 12)).padStart(4, "0")}-${pad2((idx % 12) + 1)}`;
};

/** Oldest month the history can show. */
export const minHistoryMonth = (now = new Date()) =>
  shiftMonth(currentMonthIST(now), -HISTORY_MAX_MONTHS_BACK);

/** "YYYY-MM" strings sort lexically. */
export const canGoPrevMonth = (month, now = new Date()) =>
  month > minHistoryMonth(now);

export const canGoNextMonth = (month, now = new Date()) =>
  month < currentMonthIST(now);

/** "2026-09" -> "September 2026". Empty string if unparseable. */
export const formatMonthTitle = (month) => {
  const p = parseMonth(month);
  return p ? `${MONTH_NAMES[p.m - 1]} ${p.y}` : "";
};

export const daysInMonth = (month) => {
  const p = parseMonth(month);
  return p ? new Date(Date.UTC(p.y, p.m, 0)).getUTCDate() : 0;
};

/**
 * Cells for a Sunday-first month grid: `null` blanks for the weekday offset of
 * the 1st, then one `{ date, day }` per day.
 */
export const buildMonthGrid = (month) => {
  const p = parseMonth(month);
  if (!p) return [];
  const lead = new Date(Date.UTC(p.y, p.m - 1, 1)).getUTCDay();
  const cells = Array.from({ length: lead }, () => null);
  const total = daysInMonth(month);
  for (let day = 1; day <= total; day += 1) {
    cells.push({ date: `${month}-${pad2(day)}`, day });
  }
  return cells;
};

export const HISTORY_COLORS = Object.freeze({
  complete: "#1D9B50",
  inOnly: "#F5A623",
  absent: "#E3183C",
  neutralBorder: "#DDDDE1",
  neutralText: "#8A8A8F",
  onColor: "#FFFFFF",
  onAmber: "#121212",
  todayRing: "#121212",
});

/** Fill / text / border colours for a day status. Unknown statuses read as neutral. */
export const statusStyle = (status) => {
  switch (status) {
    case HISTORY_STATUS.COMPLETE:
      return {
        backgroundColor: HISTORY_COLORS.complete,
        textColor: HISTORY_COLORS.onColor,
        borderColor: HISTORY_COLORS.complete,
      };
    case HISTORY_STATUS.IN_ONLY:
      return {
        backgroundColor: HISTORY_COLORS.inOnly,
        textColor: HISTORY_COLORS.onAmber,
        borderColor: HISTORY_COLORS.inOnly,
      };
    case HISTORY_STATUS.ABSENT:
      return {
        backgroundColor: HISTORY_COLORS.absent,
        textColor: HISTORY_COLORS.onColor,
        borderColor: HISTORY_COLORS.absent,
      };
    default:
      return {
        backgroundColor: "transparent",
        textColor: HISTORY_COLORS.neutralText,
        borderColor: HISTORY_COLORS.neutralBorder,
      };
  }
};

/** Counts of full / in-only / absent days in a `days` array. */
export const summarizeMonth = (days) => {
  const counts = { full: 0, inOnly: 0, absent: 0 };
  (Array.isArray(days) ? days : []).forEach((d) => {
    if (d?.status === HISTORY_STATUS.COMPLETE) counts.full += 1;
    else if (d?.status === HISTORY_STATUS.IN_ONLY) counts.inOnly += 1;
    else if (d?.status === HISTORY_STATUS.ABSENT) counts.absent += 1;
  });
  return counts;
};

/** "18 full · 3 in only · 4 absent". */
export const formatSummary = (days) => {
  const { full, inOnly, absent } = summarizeMonth(days);
  return `${full} full · ${inOnly} in only · ${absent} absent`;
};
